import React, { useState, useEffect, useRef } from 'react';
import './ChatBot.css';
import ApiService from '../../services/api';

// Function to parse and render markdown
const parseMarkdown = (text) => {
  if (!text) return '';
  
  // Convert markdown to formatted text
  let html = text;
  
  // Headers: ### to <h3>, ## to <h2>, # to <h1>
  html = html.replace(/^### (.*?)$/gm, '<h4 style="margin: 12px 0 8px 0; font-size: 1.05rem; font-weight: 700; color: #222;">$1</h4>');
  html = html.replace(/^## (.*?)$/gm, '<h3 style="margin: 16px 0 10px 0; font-size: 1.15rem; font-weight: 700; color: #222;">$1</h3>');
  html = html.replace(/^# (.*?)$/gm, '<h2 style="margin: 18px 0 12px 0; font-size: 1.25rem; font-weight: 700; color: #222;">$1</h2>');
  
  // Bold: **text** to <strong>
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 700; color: #222;">$1</strong>');
  
  // Italic: *text* to <em>
  html = html.replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>');
  
  // Links: [text](url) to <a>
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #0d9488; text-decoration: underline; font-weight: 600;">$1</a>');
  
  // Raw URLs: https://... or http://... to <a>
  html = html.replace(/(\bhttps?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #0d9488; text-decoration: underline; font-weight: 600; word-break: break-all;">$1</a>');
  
  // Line breaks
  html = html.replace(/\n/g, '<br/>');
  
  // Lists: * item to bullet points
  html = html.replace(/^\* (.*?)$/gm, '<div style="margin-left: 16px; margin-bottom: 8px;"><span style="display: inline-block; margin-right: 8px;">•</span>$1</div>');
  
  // Numbers: 1. item to ordered list
  html = html.replace(/^\d+\. (.*?)$/gm, '<div style="margin-left: 16px; margin-bottom: 8px;">$&</div>');

  return html;
};

function ChatBot({ setCurrentPage }) {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'Hello! 👋 I\'m your Transcontinental Network assistant. How can I help you today? Feel free to ask me about our products, services, or anything else you\'d like to know!',
      timestamp: new Date()
    },
    {
      id: 2,
      type: 'suggestions',
      suggestions: [
        { label: 'Products', value: 'What products do you have?' },
        { label: 'Services', value: 'What services do you offer?' },
        { label: 'Installation', value: 'Do you install cameras?' },
        { label: 'Contact', value: 'How can I contact you?' }
      ],
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom only on initial load
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  };

  useEffect(() => {
    // Only scroll on first load
    scrollToBottom();
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!inputValue.trim()) {
      return;
    }

    const value = inputValue;
    setInputValue('');
    await executeSendMessage(value);
  };

  const handleSuggestionClick = async (suggestionValue) => {
    if (isLoading) return;
    await executeSendMessage(suggestionValue);
  };

  const executeSendMessage = async (messageText) => {
    // Add user message to chat
    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      text: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const response = await ApiService.sendChatMessage(messageText, sessionId);

      if (response.success) {
        // Store session ID for future messages
        if (response.session_id && !sessionId) {
          setSessionId(response.session_id);
        }

        // Add bot response
        const botMessage = {
          id: messages.length + 2,
          type: 'bot',
          text: response.message,
          timestamp: new Date(response.timestamp || Date.now())
        };

        setMessages(prev => [...prev, botMessage]);
      } else {
        throw new Error('Failed to get response from chatbot');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to send message. Please try again.');

      // Add error message
      const errorMessage = {
        id: messages.length + 2,
        type: 'error',
        text: `Sorry, I encountered an error: ${err.message}. Please try again or contact us at 347.668.2160`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        id: 1,
        type: 'bot',
        text: 'Hello! 👋 I\'m your Transcontinental Network assistant. How can I help you today? Feel free to ask me about our products, services, or anything else you\'d like to know!',
        timestamp: new Date()
      }
    ]);
    setSessionId(null);
    setError(null);
  };

  return (
    <div className="chatbot-page">
      <div className="chatbot-container">
        {/* Main Chat Area */}
        <div className="chatbot-main">
        {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-content">
              <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="#0d9488" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" className="chat-header-svg">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Chat with Us
              </h1>
            </div>
            <div className="header-icons">
              <button
                className="clear-icon-btn"
                onClick={handleClearChat}
                disabled={isLoading}
                title="Clear chat history"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className="header-icon-svg">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
              <button
                className="close-icon-btn"
                onClick={() => setCurrentPage('home')}
                title="Close chat"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" className="header-icon-svg">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

        {/* Messages Container */}
        <div className="chatbot-messages">
          {messages.map((msg) => {
            if (msg.type === 'suggestions') {
              return (
                <div key={msg.id} className="message message-suggestions" style={{ alignSelf: 'flex-start', margin: '4px 0 12px 0' }}>
                  <div className="suggestions-chips-container" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {msg.suggestions.map((sug, idx) => (
                      <button
                        key={idx}
                        className="suggestion-chip"
                        onClick={() => handleSuggestionClick(sug.value)}
                        style={{
                          background: '#f0fdfa',
                          border: '1px solid #ccfbf1',
                          color: '#0d9488',
                          padding: '8px 16px',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 6px rgba(13, 148, 136, 0.05)'
                        }}
                      >
                        {sug.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            }
            return (
              <div key={msg.id} className={`message message-${msg.type}`}>
                <div className="message-bubble">
                  <div 
                    dangerouslySetInnerHTML={{
                      __html: parseMarkdown(msg.text)
                    }}
                  />
                </div>
                <div className="message-time">
                  {msg.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            );
          })}

          {isLoading && (
            <div className="message message-bot">
              <div className="message-bubble typing">
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Error Display */}
        {error && (
          <div className="chatbot-error">
            <p>{error}</p>
          </div>
        )}

        {/* Input Area */}
        <div className="chatbot-input-area">
          <form onSubmit={handleSendMessage} className="chatbot-input-form">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type message..."
              disabled={isLoading}
              className="chatbot-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={isLoading || !inputValue.trim()}
              className="chatbot-send-btn"
              title="Send"
            >
              ➤
            </button>
          </form>
        </div>
        </div>

        {/* Suggestions Sidebar */}
        <div className="chatbot-suggestions">
          <p>💡 Suggested Inquiries</p>
          <div className="suggestion-buttons">
            <button className="suggestion-btn" onClick={() => handleSuggestionClick('What products do you have?')} disabled={isLoading}>Products Catalog</button>
            <button className="suggestion-btn" onClick={() => handleSuggestionClick('What services do you offer?')} disabled={isLoading}>Services & Security</button>
            <button className="suggestion-btn" onClick={() => handleSuggestionClick('Do you install cameras?')} disabled={isLoading}>Surveillance Installation</button>
            <button className="suggestion-btn" onClick={() => handleSuggestionClick('How can I contact you?')} disabled={isLoading}>Contact Info</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatBot;
